import { useEffect, useState } from 'react';
import {
  MusicNotesSimple,
  CheckCircle,
  SpinnerGap,
  WarningCircle,
} from '../design-system/icons';
import { AUDIO_VERIFY_DEBOUNCE_MS, useResolveOfficialAudio } from '@discovr/features';
import type { ResolvedAudio } from '@discovr/types';
import { isAudioUrl } from './audioUrl';

const AUDIO_PLACEHOLDER = 'https://www.instagram.com/reels/audio/…';

interface CampaignAudioSectionProps {
  /** The verified audio URL held in the form (empty when unset). */
  value: string;
  /** Persist the verified URL (or '' to clear) back to the form. */
  onChange: (url: string) => void;
  /** Persist the resolved song/artist (or null to clear) — sent at create so the
   *  backend stores the campaign's song name reliably (no flaky re-resolve). */
  onResolved?: (
    track: { audio_id: string; song_name?: string; artist_name?: string; cover_url?: string } | null,
  ) => void;
}

/**
 * "Campaign audio" — the Official Audio Compliance setup card in Create
 * Campaign → Deliverables (Instagram only). A toggle reveals an IG audio-URL
 * input; Verify resolves it to track/artist/cover via the backend and shows a
 * confirmed card. The form carries the verified URL + resolved track, sent
 * as part of the same create-campaign body (campaignSchema.ts's
 * buildCampaignPayload) rather than a separate post-create call.
 */
export function CampaignAudioSection({ value, onChange, onResolved }: CampaignAudioSectionProps) {
  const [enabled, setEnabled] = useState(() => value.trim() !== '');
  const [input, setInput] = useState(value);
  const [meta, setMeta] = useState<ResolvedAudio | null>(null);

  const verified = value.trim() !== '' && meta !== null;

  // Lift a resolved audio's song/artist into the form's onResolved shape.
  const notifyResolved = (resolved: ResolvedAudio) =>
    onResolved?.({
      audio_id: resolved.audio_id,
      song_name: resolved.track_name,
      artist_name: resolved.artist_name,
      cover_url: resolved.cover_url,
    });

  const { status, errorMessage, resolve, resolveSilently, reset: resetResolve } = useResolveOfficialAudio(
    (resolved, url) => {
      setMeta(resolved);
      onChange(resolved.audio_url || url);
      notifyResolved(resolved);
    },
  );

  // Restore the verified card on (re)mount when a URL is already saved in the
  // form — leaving and returning to this step shouldn't force a redundant
  // re-verify or make the setting look like it didn't stick. Silent (no
  // loading/error UI): the value is already valid, this just fetches its
  // display metadata. `input` already initialises to `value`.
  useEffect(() => {
    const url = value.trim();
    if (!url || meta) return;
    resolveSilently(url, (resolved) => {
      setMeta(resolved);
      notifyResolved(resolved);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    if (!next) {
      // Turning off clears everything; invalidate any in-flight resolve so it
      // can't repopulate the value the user just cleared.
      resetResolve();
      setInput('');
      setMeta(null);
      onChange('');
      onResolved?.(null);
    }
  };

  // Auto-verify: resolve a valid audio URL shortly after it's pasted/typed, so the
  // brand never has to click a button. Debounced; the request-id guard in the
  // shared hook ignores superseded results. Fires only for a complete, valid
  // audio URL — a partial paste just waits.
  useEffect(() => {
    const url = input.trim();
    if (!enabled || verified || status === 'loading' || !isAudioUrl(url)) return;
    // The restore-on-mount effect above already resolves this exact URL when
    // it's the one already saved in the form — without this, both effects
    // fire together on mount (a saved draft resolves the same audio twice).
    // Only skips the URL currently `value` holds, so editing to something
    // different still auto-verifies normally.
    if (url === value.trim()) return;
    const timer = setTimeout(() => resolve(url), AUDIO_VERIFY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, enabled]);

  // Manual retry (used only from the error state — re-attempting the SAME URL
  // won't re-trigger the auto-verify effect since the input didn't change).
  const retry = () => {
    const url = input.trim();
    if (isAudioUrl(url)) resolve(url);
  };

  const change = () => {
    resetResolve(); // invalidate any in-flight resolve
    // Clear (not restore) the input: the committed URL is still a valid audio
    // URL, and the auto-verify effect below fires on any valid URL once
    // `verified` is false — restoring it here would re-resolve the SAME url
    // 600ms later and snap straight back to the verified card, making Change
    // look broken. Mirrors CampaignEditAudioField's resetInput().
    setInput('');
    setMeta(null);
    onChange('');
    onResolved?.(null);
  };

  return (
    <div className="w-full">
      {/* Section head + toggle */}
      <div className="flex items-start justify-between pb-4">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2">
            <MusicNotesSimple size={14} className="shrink-0 text-foreground" />
            <span className="text-body-md-medium text-foreground">Campaign audio</span>
            <span className="text-caption-regular text-muted-foreground">optional</span>
          </span>
          <span className="text-caption-regular text-muted-foreground">
            Set a common sound all creators use in their videos.
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Enable campaign audio"
          onClick={toggle}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none ${
            enabled ? 'bg-background-primary-default' : 'bg-muted'
          }`}
        >
          <span
            className={`absolute top-0.5 size-5 rounded-full bg-card shadow-sm transition-all duration-150 ${
              enabled ? 'right-0.5' : 'left-0.5'
            }`}
          />
        </button>
      </div>

      {enabled && !verified && (
        <>
          <div className="flex items-center gap-2.5">
            <input
              type="url"
              inputMode="url"
              aria-label="Instagram audio link"
              aria-invalid={status === 'error'}
              aria-describedby={status === 'error' ? 'campaign-audio-error' : undefined}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (status === 'error') resetResolve();
              }}
              placeholder={AUDIO_PLACEHOLDER}
              disabled={status === 'loading'}
              className={`h-9 min-w-0 flex-1 rounded-md border bg-card px-3 text-body-sm-regular text-foreground outline-none transition-all placeholder:text-muted-foreground focus:border-foreground focus:ring-2 focus:ring-ring/10 ${
                status === 'error' ? 'border-rose-400' : 'border-border'
              }`}
            />
            {status === 'loading' && (
              <span className="inline-flex h-9 shrink-0 items-center gap-1.5 pr-1 text-body-sm-medium text-muted-foreground">
                <SpinnerGap size={16} className="animate-spin" />
                Verifying&hellip;
              </span>
            )}
          </div>
          {status === 'error' && (
            <p
              id="campaign-audio-error"
              role="alert"
              className="mt-1.5 flex items-center gap-1 text-caption-regular text-rose-600"
            >
              <WarningCircle size={13} weight="fill" />
              {errorMessage}
              {isAudioUrl(input) && (
                <button
                  type="button"
                  onClick={retry}
                  className="ml-1 font-medium text-foreground underline underline-offset-2 hover:opacity-80"
                >
                  Retry
                </button>
              )}
            </p>
          )}
        </>
      )}

      {enabled && verified && meta && (
        <div className="flex items-center gap-3 rounded-[10px] border border-border bg-card p-3">
          <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
            {meta.cover_url ? (
              <img src={meta.cover_url} alt="" className="size-full object-cover" />
            ) : (
              <MusicNotesSimple size={18} className="text-muted-foreground" />
            )}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-body-sm-medium text-foreground">
              {meta.track_name || `Audio · ${meta.audio_id}`}
            </span>
            <span className="truncate text-caption-regular text-muted-foreground">
              {meta.artist_name || (meta.resolved ? 'Original audio' : 'Verified audio')}
            </span>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-caption-medium text-emerald-700">
            <CheckCircle size={13} weight="fill" />
            Verified
          </span>
          <button
            type="button"
            onClick={change}
            className="shrink-0 rounded-full px-3 py-1 text-body-sm-medium text-foreground hover:bg-muted"
          >
            Change
          </button>
        </div>
      )}
    </div>
  );
}
